import { Box, CircularProgress, Typography } from "@mui/material";
import { useGetUserQuery, useLoginMutation } from "features/auth/auth";
import { useAppSelector } from "features/store";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import CustomButton from "./common/CustomButton";
import CustomPasswordField from "./common/CustomPasswordField";
import CustomTextField from "./common/CustomTextField";
import ForgotPasswordModal from "./ForgotPasswordModal";

type LoginInputs = {
  username: string;
  password: string;
};

export default function Login() {
  const [login, { isLoading, error, isSuccess }] = useLoginMutation();
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  const navigate = useNavigate();
  const { isAuthenticated, user, webAccessDenied } = useAppSelector((state) => state.auth);
  const { isFetching: isUserLoading } = useGetUserQuery(undefined, {
    skip: !isAuthenticated || !!webAccessDenied,
  });

  const isBusy = isLoading || isUserLoading;
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LoginInputs>();

  const usernameValue = watch("username", "");

  const onSubmit: SubmitHandler<LoginInputs> = (data) => {
    login(data);
  };

  // Redirect already authenticated users to home page
  useEffect(() => {
    if (isAuthenticated && user && !user.is_pass_reset) {
      navigate("/");
    }
  }, [isAuthenticated, user, navigate]);

  // Navigate to home page when login is successful
  useEffect(() => {
    if (isSuccess && isAuthenticated && user && !user.is_pass_reset) {
      navigate("/");
    }
  }, [isSuccess, isAuthenticated, user, navigate]);

  // Don't render login form if user is already authenticated
  if (isAuthenticated && user && !user.is_pass_reset) {
    return null;
  }

  return (
    <Box
      marginTop={12}
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{
        width: "100%",
        maxWidth: 400,
        boxSizing: "border-box",
        px: { xs: 2, sm: 3 },
        bgcolor: "#fff",
        p: { xs: 2, sm: 4 },
        borderRadius: 1.5,
        border: "1px solid #e0e0e0",
      }}
    >
      <Typography
        component="h1"
        variant="h5"
        sx={{ fontSize: "32px" }}
        className="mb-4  whitespace-nowrap text-[rgb(31,93,107)]"
      >
        CalibrateHCM Sign In{" "}
      </Typography>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        marginTop={1}
      >
        <CustomTextField
          id="username"
          label="Username"
          autoComplete="username"
          autoFocus
          error={Boolean(errors.username)}
          helperText={errors.username ? errors.username.message : ""}
          {...register("username", {
            required: "Username is required",
          })}
        />
        <CustomPasswordField
          label="Password"
          id="password"
          autoComplete="current-password"
          error={Boolean(errors.password)}
          helperText={errors.password ? errors.password.message : ""}
          {...register("password", {
            required: "Password is required",
            // minLength: {
            //   value: 6,
            //   message: "Password must be at least 6 characters",
            // },
          })}
        />
        {isBusy ? (
          <div className="mt-4 flex justify-center">
            <CircularProgress />
          </div>
        ) : (
          <CustomButton type="submit">Sign In</CustomButton>
        )}

        {error && (
          <Typography
            color="error"
            style={{ marginTop: "16px", textAlign: "center" }}
          >
            Invalid Username or Password. Please try again.
          </Typography>
        )}

        <Box display="flex" justifyContent="center" mt={2}>
          <Typography
            component="span"
            sx={{
              color: "rgb(31, 93, 107)",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() => setForgotPasswordOpen(true)}
          >
            Forgot Password?
          </Typography>
        </Box>
      </Box>

      <ForgotPasswordModal
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
        initialUsername={usernameValue}
      />
    </Box>
  );
}
